/**
 * Cấu hình → "Cài đặt tiệm" (chỉ chủ tiệm): đặt lịch trên Zalo (N1) + quyền của nhân viên.
 * Bật/tắt tính năng nằm ở mục riêng "Tính năng của tiệm" (TinhNang.tsx, ADR-074).
 * Mỗi công tắc lưu NGAY khi bấm (không có nút "Áp dụng") — giống cách chủ tiệm bật Wi-Fi.
 */
import { useEffect, useState } from 'react'
import { type CaiDatTiem, caiDatApi, congTacApi, type QuyenTho } from './api.ts'
import { datCaiDat, useCaiDatTiem, useTinhNang } from './cai-dat-tiem.ts'
import { T } from './text/vi.ts'
import { CongTac, Xuong } from './ui.tsx'

type Sua = Partial<CaiDatTiem['datLich']>

/** Mốc nhắc lịch và hạn xác nhận chọn nhanh — không ô nhập số. */
const HAN_CHON = [60, 120, 180, 360] as const

export function TheCaiDatTiem({
  onLoi,
  onXong,
}: {
  onLoi: (e: unknown) => void
  onXong: (cau: string) => void
}) {
  const c = useCaiDatTiem()
  const [dang, setDang] = useState(false)

  async function luu(sua: Sua) {
    setDang(true)
    try {
      datCaiDat(await caiDatApi.luu(sua))
      onXong(T.saved)
    } catch (e) {
      onLoi(e)
    } finally {
      setDang(false)
    }
  }

  if (c === null) return <Xuong cao={240} />
  return (
    <>
      <section className="the the-dem the-khoi">
        <h2>{T.setBookingTitle}</h2>
        <p className="chu-phu">{T.setBookingHint}</p>
        <div className="ds-cong-tac">
          <CongTac
            bat={c.datLich.tuDatLich}
            tat={dang}
            nhan={T.setSelfBooking}
            onDoi={(bat) => void luu({ tuDatLich: bat })}
          />
          <CongTac
            bat={c.datLich.duyetTay}
            tat={dang || !c.datLich.tuDatLich}
            nhan={T.setManualApprove}
            onDoi={(bat) => void luu({ duyetTay: bat })}
          />
        </div>
        <div className="truong">
          <span className="nhan">{T.setRemindWhen}</span>
          <div className="hang-chip chia-deu cot-2">
            {(['truoc_24h', 'sang_cung_ngay'] as const).map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={c.datLich.mocNhac === m}
                className={`chip ${c.datLich.mocNhac === m ? 'dang-chon' : ''}`}
                disabled={dang}
                onClick={() => void luu({ mocNhac: m })}
              >
                {m === 'truoc_24h' ? T.setRemind24h : T.setRemindMorning}
              </button>
            ))}
          </div>
        </div>
        <div className="truong">
          <span className="nhan">{T.setConfirmDeadline}</span>
          <div className="hang-chip chia-deu cot-4">
            {HAN_CHON.map((p) => (
              <button
                key={p}
                type="button"
                aria-pressed={c.datLich.hanXacNhanPhut === p}
                className={`chip ${c.datLich.hanXacNhanPhut === p ? 'dang-chon' : ''}`}
                disabled={dang}
                onClick={() => void luu({ hanXacNhanPhut: p })}
              >
                {T.setHoursBefore(p / 60)}
              </button>
            ))}
          </div>
        </div>
      </section>

      <TheQuyenTho onLoi={onLoi} onXong={onXong} />
    </>
  )
}

/**
 * Mức trần giảm giá chủ tiệm chọn (basis point). Không có ô gõ số: 5 mức là đủ, và không gõ nhầm 100%.
 */
const MUC_GIAM_BP = [0, 1000, 2000, 3000, 5000] as const

/**
 * Quyền của nhân viên (phản ánh 25/09 — trước là "Quyền của thợ"; thêm 2 công tắc máy chủ đã có).
 * Quyền của thợ (RETRO bảo mật 7): "thấy số" và "xem / chụp ảnh" là HAI công tắc — trước đây muốn thợ chụp
 * ảnh buổi làm là phải cho mọi thợ thấy đủ số của mọi khách. Mặc định tắt cả hai.
 */
function TheQuyenTho({
  onLoi,
  onXong,
}: {
  onLoi: (e: unknown) => void
  onXong: (cau: string) => void
}) {
  const [q, setQ] = useState<QuyenTho | null>(null)
  const [coMaDuyet, setCoMaDuyet] = useState(false)
  const [maDuyet, setMaDuyet] = useState('')
  const [dang, setDang] = useState(false)
  // Quản lý / Lễ tân chỉ có khi bật "Phân quyền chi tiết" (ADR-074) → hai cài đặt của họ ẩn theo.
  const coVaiChiTiet = useTinhNang('phan-quyen')
  // biome-ignore lint/correctness/useExhaustiveDependencies: tải MỘT lần khi mở thẻ — `onLoi` của cha có thể đổi mỗi lần vẽ.
  useEffect(() => {
    congTacApi
      .doc()
      .then((r) => {
        setQ(r.congTac)
        setCoMaDuyet(r.coMaDuyet === true)
      })
      .catch(onLoi)
  }, [])

  async function luuMaDuyet() {
    setDang(true)
    try {
      await congTacApi.datMaDuyet(maDuyet)
      setCoMaDuyet(true)
      setMaDuyet('')
      onXong(T.setApproveSaved)
    } catch (e) {
      onLoi(e)
    } finally {
      setDang(false)
    }
  }

  async function luu(sua: Partial<QuyenTho>) {
    setDang(true)
    try {
      setQ((await congTacApi.luu(sua)).congTac)
      onXong(T.saved)
    } catch (e) {
      onLoi(e)
    } finally {
      setDang(false)
    }
  }

  if (q === null) return <Xuong cao={120} />
  return (
    <section className="the the-dem the-khoi">
      <h2>{T.setStaffTitle}</h2>
      <p className="chu-phu">{T.setStaffHint}</p>
      <div className="ds-cong-tac">
        <CongTac
          bat={q.ktvXemSdt}
          tat={dang}
          nhan={T.setStaffPhone}
          onDoi={(bat) => void luu({ ktvXemSdt: bat })}
        />
        <CongTac
          bat={q.ktvXemAnh}
          tat={dang}
          nhan={T.setStaffPhotos}
          onDoi={(bat) => void luu({ ktvXemAnh: bat })}
        />
        <CongTac
          bat={q.ktvTuThuTien}
          tat={dang}
          nhan={
            <>
              {T.setStaffCheckout}
              <span className="ghi-chu-16 dong-rieng">{T.setStaffCheckoutHint}</span>
            </>
          }
          onDoi={(bat) => void luu({ ktvTuThuTien: bat })}
        />
        {coVaiChiTiet && (
          <CongTac
            bat={q.quanLyXemDoanhThu}
            tat={dang}
            nhan={T.setManagerRevenue}
            onDoi={(bat) => void luu({ quanLyXemDoanhThu: bat })}
          />
        )}
      </div>
      {/* Trần giảm giá áp cho lễ tân và thợ tự thu (ADR-078) — hiện khi có một trong hai. */}
      {(coVaiChiTiet || q.ktvTuThuTien) && (
        <div className="truong">
          <span className="nhan">{T.setDiscountMax}</span>
          <div className="hang-chip">
            {MUC_GIAM_BP.map((bp) => (
              <button
                key={bp}
                type="button"
                disabled={dang}
                aria-pressed={q.giamGiaToiDaBp === bp}
                className={`chip ${q.giamGiaToiDaBp === bp ? 'dang-chon' : ''}`}
                onClick={() => void luu({ giamGiaToiDaBp: bp })}
              >
                {T.setDiscountPct(bp)}
              </button>
            ))}
          </div>
          <p className="chu-phu">{T.setDiscountHint}</p>
          {/* KH-GĐ2 6.2.4: mã riêng để chủ tiệm duyệt giảm vượt mức ngay tại quầy — KHÔNG phải mã đăng nhập. */}
          <label className="nhan" htmlFor="ma-duyet-moi" style={{ marginTop: 16 }}>
            {coMaDuyet ? T.setApproveChange : T.setApproveNew}
          </label>
          <div className="hang-chip">
            <input
              id="ma-duyet-moi"
              className="o-nhap o-ma-an"
              style={{ flex: 1 }}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={12}
              value={maDuyet}
              onChange={(e) => setMaDuyet(e.target.value.replace(/\D/g, ''))}
            />
            <button
              type="button"
              className="nut nut-vien"
              disabled={dang || maDuyet.length < 6}
              onClick={() => void luuMaDuyet()}
            >
              {T.setApproveSave}
            </button>
          </div>
          <p className="chu-phu">{coMaDuyet ? T.setApproveHas : T.setApproveHint}</p>
        </div>
      )}
    </section>
  )
}
